/**
 * World Agendas (docs/design-agendas.json). Each nation authors one resolution with a Full and an Amended
 * version, a reserved Assembly seat, a dilemma, a doctrine column (how every other nation's beliefs lean on
 * it) and effect numbers. Every tunable number lives here so scripts/simulate.ts can tune it.
 *
 * Student text rules: plain words (Flesch-Kincaid grade 8.5 or lower), no emoji, and the resolution text,
 * lead lines and speech never name a country other than the author. Burdens fall on conditions (Carbon 3 or
 * more, a pirate-exposed coast, farmland with a Food surplus, drawing or not drawing, host or home of
 * displaced families), never on an ideology.
 */
import type { CountryId, ResourceBundle, SlotId } from '../../shared/types.ts';

export interface AgendaData {
  id: CountryId;
  author: CountryId;
  title: string;
  amendedTitle: string;
  /** Suggested meeting (the reserved seat). */
  reservedTurn: number;
  /** The three lead lines of an agenda card (the third line, votes needed, is built by the engine). */
  lead: { does: string; costs: string };
  fullText: string;
  amendedText: string;
  /** Prefilled 60-second speech (3 sentences). */
  speech: string;
  /** Private to the author: why this matters to us, in game units. */
  whyUs: string;
  /** Private to the author: the dilemma, a tradeoff between two real values. */
  dilemma: string;
  whoPays: string;
  /** Private to the author: bargaining chips. */
  chips: string;
  naturalAllies: CountryId[];
  naturalOpponents: CountryId[];
  /** Agendas this one pulls against, and agendas it helps (by author id). */
  conflicts: CountryId[];
  complements: CountryId[];
  /**
   * Doctrine column: how each other nation's beliefs lean on this agenda (-2..+2). Every column and every
   * row of the table sums to +1, so no agenda is structurally easier and no voter is the default "no".
   */
  doctrine: Partial<Record<CountryId, number>>;
  /** What an AI author offers (escrowed) for a Full pledge, at most MAX_PAID_OFFERS times. */
  lobbyOffer: ResourceBundle;
  /** The exposure bars this agenda answers (keys of the per-pairing exposure table). */
  exposure: ExposureKey[];
}

export type ExposureKey = 'climate_s3' | 'climate_s4' | 'piracy' | 'trade_squeeze' | 'food_loss' | 'arctic' | 'oil_decline' | 'displacement' | 'drought';

export const EXPOSURE_LABELS: Record<ExposureKey, string> = {
  climate_s3: 'Expected climate loss per turn at Danger (Stage 3)',
  climate_s4: 'Expected climate loss per turn at Breakdown (Stage 4)',
  piracy: 'Expected pirate losses per turn while the seas are unsafe',
  trade_squeeze: 'Treasury income per turn from land and trade',
  food_loss: 'Steady Food loss per turn from heat at Stage 3',
  arctic: 'Arctic risks per turn from Stage 2 (warships, spills, thaw)',
  oil_decline: 'Treasury lost per turn when oil prices fall',
  displacement: 'Families forced from home at the refugee crisis',
  drought: 'Food lost to drought at Stage 3',
};

/** An adaptation counts toward "cost to reach full adaptation" if it cuts expected loss per turn by this much. */
export const EXPOSURE_WORTHWHILE = 0.5;

/* ---------------------------------------------------------------------------------------------- */
/* Procedure numbers                                                                               */
/* ---------------------------------------------------------------------------------------------- */

/** Votes needed by world size. One nation, one vote (the Atoll's extra climate vote does not apply). */
export const AGENDA_THRESHOLDS: Record<number, { full: number; amended: number }> = {
  8: { full: 6, amended: 5 },
  7: { full: 6, amended: 5 },
  6: { full: 5, amended: 4 },
};

export function thresholdsFor(n: number): { full: number; amended: number } {
  return AGENDA_THRESHOLDS[n] ?? { full: Math.ceil(n * 0.75), amended: Math.ceil(n * 0.625) };
}

export const MAX_FIRST_READINGS = 3;
export const MAX_VOTE_ONLY = 2;
/** Paid pledge offers per agenda, for every author (human or AI) and every other proposer. */
export const MAX_PAID_OFFERS = 3;
export const MAX_COSPONSORS = 3;
/** Agendas each nation may co-sponsor per game. */
export const MAX_COSPONSORSHIPS = 2;
/** Costs, caps and bans apply from this many turns after the passing meeting. */
export const EFFECTIVE_OFFSET = 1;

/** Reserved first-reading seats. T17 stays open for moved agendas, second readings and upgrades. */
export const AGENDA_DOCKET: Record<CountryId, number> = {
  vellmark: 5,
  kestran: 5,
  meridia: 5,
  ironvale: 9,
  brightwater: 9,
  aurem: 9,
  alvera: 13,
  karsova: 13,
};

export const AGENDA_SCORING = {
  authorFull: 6,
  authorAmended: 3,
  cosponsorFull: 2,
  cosponsorAmended: 1,
  supportFull: 4,
  supportAmended: 2,
  supportFail: -2,
  /** A win at home: every one of the author's four index scores rises by this much at passage. */
  indexFull: 3,
  indexAmended: 2,
} as const;

/** The universal agenda vote rule for bots (no per-country special cases). */
export const AGENDA_AI = {
  impactDivisor: 3,
  impactClamp: 3,
  impactCap: 9,
  /** Share of the projected climate change (from the Heat Meter projection) counted in impact: a projection is an estimate. */
  climateWeight: 0.35,
  cosponsorBonus: 2,
  treatyBonus: 1,
  brokerBonus: 1,
  isolationistPenalty: -1,
  /** Minus this when the author's displayed points are leaderGap or more above the world mean. */
  leaderPenalty: 3,
  /** Each bot's per-game conviction on each agenda is a fixed seeded number in [-spread, +spread]. */
  convictionSpread: 1.5,
  fullAt: 1.5,
  amendedAt: 1,
  /** Minimum value of a paid pledge offer, and the per-doctrine-point premium. */
  pledgeMinValue: 2,
  /** Price = max(pledgeMinValue, pledgeBase - impact + pledgeDoctrinePremium x doctrine against). */
  pledgeBase: 2,
  pledgeDoctrinePremium: 2,
  /** AI authors offer a voter its price, from the template amount up to this many units. */
  lobbyMax: 5,
  /** Paid pledges one bot accepts per meeting, across all agendas. */
  paidPledgesPerMeeting: 1,
  /** AI authors lobby voters whose score is at least amendedAt minus this. */
  lobbyReach: 2,
  /** In a package deal a bot pledges a vote at most this far above its own score's threshold. */
  packageStretch: 1,
  /** Core Four proxy: percent of agenda votes a proxy-human seat skips (counts as Abstain). */
  proxySkipPercent: 15,
} as const;

/**
 * Per-mode offsets added to every voter's score on an agenda (simulator-tuned, default 0). 'ai' is a world of
 * bots; 'core_four' is a world where the Core Four seats are human (or proxy-human in the simulator).
 */
export const MODE_OFFSET: Record<'ai' | 'core_four', Partial<Record<CountryId, number>>> = {
  ai: { vellmark: -0.9, kestran: -0.45, meridia: -0.75, ironvale: -0.5, brightwater: -1.95, aurem: -0.65, alvera: -0.85, karsova: -0.8 },
  core_four: { vellmark: -0.8, kestran: -0.35, meridia: -0.7, ironvale: -0.5, brightwater: -1.9, aurem: -0.6, alvera: -0.85, karsova: -0.65 },
};

/**
 * Per-mode, per-agenda easing of the Full line (simulator-tuned, default 0): a bot that would vote at least
 * Amended votes Full when its score plus this reaches AGENDA_AI.fullAt. It moves Amended votes to Full and never
 * turns a No into a Yes, so it tunes how often the Full version passes without changing how often either passes.
 */
export const FULL_OFFSET: Record<'ai' | 'core_four', Partial<Record<CountryId, number>>> = {
  ai: { vellmark: 0.5, kestran: 0.4, meridia: 0.5, ironvale: 0.25, brightwater: 0.5, aurem: 0.5, alvera: 0.45, karsova: 0.5 },
  core_four: { vellmark: 0.5, kestran: 0.25, meridia: 0.5, ironvale: 0.3, brightwater: 0.5, aurem: 0.5, alvera: 0.5, karsova: 0.35 },
};

/* ---------------------------------------------------------------------------------------------- */
/* Effect numbers                                                                                  */
/* ---------------------------------------------------------------------------------------------- */

/** Delta Pact (Vellmark). */
export const PACT = {
  carbonLine: 3,
  deadlineOffset: 4,
  switchDiscount: 1,
  missPay: 4,
  missSupport: -2,
  levy: 3,
  levyAmended: 1,
  authorPay: 2,
  authorPayLater: 1,
  authorPayAmended: 1,
  authorShortSupport: -2,
  rewardSupport: 2,
  rewardEnvironment: 2,
  /** The clean-tech tipping line is this much deeper (at 8 nations, scaled by n/8) while the Full Pact is in force. */
  tippingExtra: 5,
  /** The tipping point's low Energy price never starts before this turn while the Full Pact is in force. */
  lowPriceNotBefore: 10,
} as const;

/** Unsafe Seas slot rule (answered by the Joint Sea Patrol). */
export const UNSAFE_SEAS = {
  fromTurn: 4,
  slots: ['harbor', 'atoll'] as SlotId[],
  raidOn: 2,
  raidOnPatrolled: 1,
  /** Funders of the T3 patrol needed for the lower raid chance. */
  patrolFunders: 2,
  raidTreasury: -2,
  raidProsperity: -1,
  navyRaidTreasury: -1,
  navyLine: 2,
  prosperityCapPerGame: 2,
  /** While unsafe, the Harbor's Trade Hub counts at most this many treaties. */
  tradeHubUnsafe: 1,
} as const;

/** Joint Sea Patrol and Open Ports Accord (Kestran). */
export const SEAS = {
  dues: 1,
  exposedDues: 2,
  lendTurns: 3,
  shortSupport: -2,
  invasionSupport: -2,
  tradeHubFull: 3,
  authorRecordsSupport: -2,
  /** Amended: raids only on a d10 of this or less (0 with 2 or more T3 patrol funders). */
  amendedRaidOn: 1,
} as const;

/** Open Markets and Free Transit Charter (Meridia). */
export const OPEN = {
  banTurns: 4,
  banTurnsAmended: 2,
  lateTariffSupport: -2,
  standingLiftSupport: -2,
  amendedLiftSupport: -1,
  transitTreasury: 1,
  transitSlots: ['highland'] as SlotId[],
  sellCap: 5,
  fundPay: 1,
  fundGrant: 2,
  authorSupport: -2,
} as const;

/** Feed the World Pact (Ironvale). */
export const FOOD = {
  seedsFromStage: 3,
  seedsDrainCut: 1,
  buyFoodCap: 5,
  authorGive: 2,
  surplusGive: 1,
  surplusFlowLine: 1,
  otherPay: 1,
  backstopFood: 2,
  foodPriceTreasury: 2,
  farmSupportCost: 2,
  leverageBanTurns: 4,
} as const;

/** Arctic Opening slot rule (answered by the Arctic Treaty). */
export const ARCTIC_OPENING = {
  fromStage: 2,
  navyLine: 2,
  transitTreasury: 1,
  firstTransitSecurity: -2,
  securityCapPerGame: 4,
  spillOn: 1,
  spillEnvironment: -3,
} as const;

/** Arctic Peace and Permafrost Treaty (Brightwater). */
export const ARCTIC = {
  watchFeeAuthor: 3,
  watchFee: 1,
  methaneOnFull: 1,
  methaneOnAmended: 2,
  permafrostUpkeepCut: 1,
  authorSupport: -2,
  civilianNavyLine: 1,
} as const;

/** Just Transition Compact (Aurem). */
export const WINDOW = {
  pay: 1,
  authorPay: 2,
  maxDraw: 2,
  creditTurns: 2,
  drawCarbonLine: 3,
  bridgeUntil: 16,
  bridgeUntilAmended: 14,
  auremBridgeUntil: 14,
  auremBridgeCarbon: 1,
  strandedCarbonRelief: 5,
  forestCarbonLine: 2,
  /** Grand Bargain: a Pact deadline moves to the end of this turn for a producer that draws before its cut. */
  pactDeadlineWithDraw: 11,
  cutRefund: 2,
} as const;

/** Dignity in Displacement Compact (Alvera). */
export const DIGNITY = {
  fee: 3,
  feeShortSupport: -3,
  homePay: 1,
  welcomeFood: 1,
  fundTreasury: 2,
} as const;

/** Right to Stay Compact (Karsova). */
export const STAY = {
  dues: 1,
  droughtLine: 2,
  droughtCut: 1,
  irrigationFood: 1,
  irrigationFromStage: 3,
  groupsFewer: 1,
  resettlePerPayers: 2,
  resettleMax: 3,
  heatSafeCost: 1,
  authorSupport: -3,
  fallbackPay: 3,
  fallbackBanTurns: 2,
} as const;

/* ---------------------------------------------------------------------------------------------- */
/* The eight agendas                                                                               */
/* ---------------------------------------------------------------------------------------------- */

export const AGENDAS: Record<CountryId, AgendaData> = {
  vellmark: {
    id: 'vellmark',
    author: 'vellmark',
    title: 'The Delta Pact',
    amendedTitle: 'Pledge and Review',
    reservedTurn: 5,
    lead: {
      does: 'Every nation with Carbon 3 or more cuts 1 Carbon within 4 turns. New fossil projects pay a fee.',
      costs: 'High-carbon nations pay for one clean switch or a 4 Treasury penalty. The author pays into the Pact Account.',
    },
    fullText:
      'Every nation with Carbon 3 or more must cut its Carbon by 1 within 4 turns. Until then, a clean switch costs it 1 less. A nation that does not make its cut pays 4 Treasury into the climate fund and loses 2 Public Support. From next turn, any nation that opens a new fossil fuel project or clears rainforest pays 3 Treasury into the Pact Account. Vellmark puts in 2 Treasury now and 1 at each later Assembly. The money builds flood defenses in the vulnerable nation hit hardest since the last meeting.',
    amendedText:
      'Nations with Carbon 3 or more are asked to cut 1 Carbon, but no one is forced. Each one that cuts within 4 turns gains 2 Public Support and 2 Environment. New fossil projects and forest clearing cost 1 Treasury. Vellmark puts in 1 Treasury now.',
    speech:
      'Our delta sits below three rivers, and every flood starts upstream of us. The Pact asks the biggest carbon producers for one clean switch each, and it makes that switch cheaper. We pay first, and every coin buys flood walls for the nations hit hardest.',
    whyUs:
      'From Stage 3 our River flood hits often: about 4 in 10 turns, for 3 Treasury, 3 Well-being and 2 Food. Our Resilience counts at most 1 against water hits from Stage 3. We expect to lose about 5 units a turn at Stage 3, the most in the world. Full protection costs us about 13 Treasury, and we have the least room to pay.',
    dilemma:
      'We need 5 other votes for the Full Pact, but only 3 nations are not asked to cut. So we must win at least 2 of the big carbon producers the Pact asks to pay. They are hurt far less by warming than we are. To win them we must spend what our own people care about: Food our farmers want to keep, Treasury we barely have, and maybe our vote for a slower plan our Green Youth dislike. Our Business Federation also wants Prosperity of 45 or more, and we start at 39.',
    whoPays:
      'Nations with Carbon 3 or more: a cheaper clean switch now and 1 less Energy each turn after, or 4 Treasury and 2 Public Support if they do not cut. The author: 2 Treasury now and 1 at each later Assembly. Anyone who later opens fossil projects or clears forest: 3 Treasury each time.',
    chips:
      'Food: our delta holds the world’s biggest Food store, so we can offer 2 Food for a Full pledge. The Tripartite Accord can pay for a clean switch in one producer. Our free switch shows good faith. We have up to 3 co-sponsor seats, and our votes at Turn 9.',
    naturalAllies: ['alvera', 'brightwater', 'meridia'],
    naturalOpponents: ['ironvale', 'kestran', 'aurem'],
    conflicts: ['aurem', 'ironvale'],
    complements: ['brightwater', 'alvera', 'karsova'],
    doctrine: { ironvale: -1, brightwater: 1, meridia: 1, kestran: -1, alvera: 2, karsova: 0, aurem: -1 },
    lobbyOffer: { treasury: 0, energy: 0, food: 2 },
    exposure: ['climate_s3', 'climate_s4'],
  },

  kestran: {
    id: 'kestran',
    author: 'kestran',
    title: 'Joint Sea Patrol and Open Ports Accord',
    amendedTitle: 'Coastal Patrol Cooperation',
    reservedTurn: 5,
    lead: {
      does: 'A joint patrol ends piracy for the rest of the game, and busy ports earn more from trade treaties.',
      costs: 'Every nation pays 1 Treasury or lends 1 Navy unit. Coasts that pirates attack pay 2.',
    },
    fullText:
      'Nations form a Joint Sea Patrol that ends piracy on the shipping lanes for the rest of the game. Each nation pays 1 Treasury, or lends 1 Navy unit to the patrol for 3 turns. The nations whose shipping pirates attack pay 2. Patrol ships enter a nation’s waters only when invited, and any attack on another nation’s home land costs the attacker 2 extra Public Support. Ports open up: nations share cargo-check rules, and port records are open to all members. Busy ports can earn more from trade treaties.',
    amendedText:
      'No patrol and no payments. Nations share pirate warnings, so pirate raids are half as likely, and busy ports earn their normal trade income again.',
    speech:
      'Pirates stop the ships that carry our food and fuel, and they could stop yours next. A joint patrol ends the raids for good, and no warship enters any nation’s waters without an invitation. Every nation pays a little, and every trading nation gains.',
    whyUs:
      'Only two coasts face pirates, and ours is one. From Turn 4, raids hit 2 turns in 10: 2 Treasury and 1 Prosperity each time, since our Navy is small. While the seas are unsafe, our Trade Hub counts only 1 treaty. That is about 1.6 units a turn, or about 25 units by Turn 20. Six nations never face pirates at all.',
    dilemma:
      'We believe no nation should send warships into another nation’s waters. Yet ending piracy needs a joint patrol. The invitation rule protects our belief, but it may make the patrol look weak. Open port records mean our security officials must share what they keep private (-2 Public Support). We see our own Public Support only as a band. We are rich and can pay for votes. But every coin we spend abroad is a coin we do not spend on Common Prosperity at home.',
    whoPays:
      'Every nation: 1 Treasury, or 1 Navy unit for 3 turns. The two coasts that pirates attack: 2. The author also loses 2 Public Support for open port records. Any nation that attacks another nation’s home land: 2 more Public Support.',
    chips:
      'Treasury: we start with the most in the world. The Five-Year Plan (Turns 1, 5, 9, 13, 17) can move 6 units to fund a deal. Green Development Loans help high-carbon nations meet a Pact cut. Navy units for the patrol. Our later votes on food, the Arctic, the transition and displacement.',
    naturalAllies: ['ironvale', 'alvera', 'aurem'],
    naturalOpponents: ['meridia', 'karsova'],
    conflicts: ['brightwater', 'karsova'],
    complements: ['meridia', 'ironvale', 'alvera'],
    doctrine: { ironvale: 1, brightwater: 0, meridia: -1, vellmark: 0, alvera: 1, karsova: -1, aurem: 1 },
    lobbyOffer: { treasury: 2, energy: 0, food: 0 },
    exposure: ['piracy'],
  },

  meridia: {
    id: 'meridia',
    author: 'meridia',
    title: 'Open Markets and Free Transit Charter',
    amendedTitle: 'Tariff Truce',
    reservedTurn: 5,
    lead: {
      does: 'All tariffs and embargoes end, new ones are banned for 4 turns, and landlocked nations get free transit.',
      costs: 'Every nation pays 1 Treasury into a Worker Adjustment Fund. Nations that use tariffs lose them.',
    },
    fullText:
      'All tariffs and trade embargoes end now, including long-standing ones. For 4 turns no nation may start a new tariff or embargo. After that, starting one costs 2 Public Support. The Assembly can still vote for sanctions. Nations with no coast get free transit to the sea, worth 1 Treasury per turn. Every nation may sell up to 5 Energy and 5 Food per turn on the World Market instead of 4. Every nation pays 1 Treasury into a Worker Adjustment Fund, which helps any nation whose Prosperity falls during those 4 turns.',
    amendedText:
      'Long-standing tariffs stay, but lifting one costs only 1 Public Support. No new tariffs or embargoes for 2 turns. Nations with no coast get free transit, worth 1 Treasury per turn.',
    speech:
      'Every tariff takes a little from both sides of a deal, and it hurts most the nations that trade the most. This Charter ends them, gives landlocked nations a free road to the sea, and lets every nation sell more of what it grows. A small fund helps any nation whose workers feel the change.',
    whyUs:
      'Our mountain land has the weakest income of any land: 2 Treasury less each turn, because our goods must cross other borders to reach the sea. Any nation placed here would be the poorest. Tariffs cost each deal 1 unit, and our Open Seas goal needs 8 trades. We keep no central stores, so any Energy or Food above 8 must be sold.',
    dilemma:
      'We would rather every nation choose free trade on its own. We are slow to ask a world body to write trade rules, because rules made today could limit freedom tomorrow. Some nations worry about their workers. To win their votes, the Full version adds a world fund. Our Mutual Aid Societies dislike that kind of program (-2 Public Support). Our Non-Interventionists will not let us offer military help, so we must win votes with trade alone.',
    whoPays:
      'Nations that use tariffs lose them: long-standing tariffs end, with their income and 2 Public Support. Tariff bargaining is set aside for 4 turns. Every nation: 1 Treasury into the fund. The author also loses 2 Public Support (Full only). Anyone who starts a tariff after the ban: 2 Public Support.',
    chips:
      'A Treasury that grows easily (upkeep 2 lower than others). The best trade terms: +1 unit on every deal of 4 or more. The Free Port Charter: a 4-turn Free Trade Zone with one partner. Innovation Prizes make the next clean switch cheaper for anyone. Our votes on the Delta Pact and the Sea Patrol at the same meeting.',
    naturalAllies: ['vellmark', 'kestran', 'aurem'],
    naturalOpponents: ['ironvale', 'brightwater', 'alvera'],
    conflicts: ['ironvale', 'karsova', 'aurem'],
    complements: ['kestran', 'ironvale', 'karsova'],
    doctrine: { ironvale: -1, brightwater: -1, kestran: 1, vellmark: 2, alvera: -1, karsova: 0, aurem: 1 },
    lobbyOffer: { treasury: 2, energy: 0, food: 0 },
    exposure: ['trade_squeeze'],
  },

  ironvale: {
    id: 'ironvale',
    author: 'ironvale',
    title: 'Feed the World Pact',
    amendedTitle: 'Open Food Trade',
    reservedTurn: 9,
    lead: {
      does: 'Heat-hardy seeds cut steady Food losses from Stage 3, and Food can never be blocked by tariffs or embargoes.',
      costs: 'The author gives 2 Food at every Assembly. Farm nations with a surplus give 1 Food. Others pay 1 Treasury once.',
    },
    fullText:
      'Nations share heat-hardy seeds and farm know-how. Once the world reaches Danger (Stage 3), every nation’s steady Food loss from heat is 1 smaller. Food can never be blocked by tariffs or embargoes, and every nation may buy up to 5 Food per turn on the World Market. A Farm Innovation Fund makes Farm Support cost 2 for everyone and gives 2 Food to any nation the first time it runs short. Ironvale gives 2 Food to the Fund now and at each later Assembly, and it sets aside its tariff tool for 4 turns. Nations whose land grows a Food surplus give 1 Food at the same times. Every other nation pays 1 Treasury now.',
    amendedText:
      'Food can never be blocked by tariffs or embargoes, and Farm Support costs 2 for everyone. No seed sharing and no Fund.',
    speech:
      'When harvests fail anywhere, families go hungry everywhere. This Pact shares heat-hardy seeds, keeps food trade open, and backs every nation with a Food fund. We give first, at every meeting, because a fed world is a safer world.',
    whyUs:
      'Our plains are the world’s breadbasket, and heat attacks exactly that. We are the only land that loses Food every turn from Stage 2 (-1), growing to -2 at Stage 3 and -3 at Stage 4. Resilience and Water Treaties do not stop these losses. That is about 22 Food lost by Turn 20. If any nation runs short in the last 5 turns, every nation loses End Famine (+5).',
    dilemma:
      'We want to feed a hungry world through farm innovation and open food markets. To do it we must give up some bargaining power (our tariff tool, for 4 turns) and share our harvest with a world fund at every meeting. Our seeds also help rival farm nations. Our Young Conservatives want a Treasury of 8 or more, and our Workers and Producers want fair deals, so every vote we win with Energy or Food must be worth it.',
    whoPays:
      'The author: 2 Food now and at each later Assembly, and no tariffs for 4 turns. Nations whose land grows a Food surplus: 1 Food at the same times. Every other nation: 1 Treasury once. Nations with tariffs lose them on Food deals.',
    chips:
      'Energy Dominance: every Energy sale to a treaty partner earns us 1 Treasury, so we can sell Energy at friendly prices for votes. Food from the breadbasket. Peace Through Strength protects 2 treaty allies for 3 turns. Our Turn 5 votes, and our same-meeting votes on the Arctic and the transition.',
    naturalAllies: ['karsova', 'kestran'],
    naturalOpponents: ['meridia', 'vellmark'],
    conflicts: ['karsova', 'meridia', 'vellmark'],
    complements: ['karsova', 'alvera', 'brightwater'],
    doctrine: { brightwater: 0, meridia: -1, kestran: 1, vellmark: -1, alvera: 0, karsova: 2, aurem: 0 },
    lobbyOffer: { treasury: 0, energy: 2, food: 0 },
    exposure: ['food_loss'],
  },

  brightwater: {
    id: 'brightwater',
    author: 'brightwater',
    title: 'Arctic Peace and Permafrost Treaty',
    amendedTitle: 'Arctic Safety Code',
    reservedTurn: 9,
    lead: {
      does: 'The Arctic becomes a zone of peace and science. A Permafrost Watch makes methane gas leaks much less likely.',
      costs: 'The author pays 3 Treasury and gives up its lane income and new drilling. Every other nation pays 1 Treasury.',
    },
    fullText:
      'The Arctic becomes a zone of peace and science. Warships may not sail the new Arctic sea lane. Cargo ships may, if their nation has a treaty with the Arctic nation, and each trip earns them 1 Treasury. The Arctic nation shares the lane, so it no longer earns its own lane income. A Permafrost Watch tracks thawing ground, which makes a methane release much less likely and lowers repair costs by 1. Brightwater pays 3 Treasury to the Watch now, and every other nation pays 1. The Arctic nation may never open new fossil fuel projects.',
    amendedText:
      'Warships may still sail the Arctic lane but must give notice first, so the Arctic nation loses no Security. Oil spills happen less often, and methane releases are a little less likely. No fees and no drilling ban.',
    speech:
      'As the ice melts, the north opens to warships, oil spills and methane that heats every nation. This Treaty keeps the Arctic for peace and science, and it opens the lane to any nation that signs a treaty with us. We pay the most and give up our own lane income, because a cooler world is worth it.',
    whyUs:
      'Only our land has permafrost and an Arctic coast. From Stage 2 the melting ice opens a sea lane: it pays us at first, but it opens our coast to foreign warships (-2 Security the first time each foreign navy sails through) and oil spills (-3 Environment, 1 in 10 per trip). From Stage 3 thawing ground adds repair costs, and each turn there is a 3 in 10 chance of a methane release that heats the whole world.',
    dilemma:
      'Our northern land sees some short-term gains as the ice melts, and our energy pays for good jobs. A lasting Arctic peace means giving some of that up: our lane income, 3 Treasury for the Watch, and any new drilling. Our Climate and Youth Movement will cheer, but our Labor and Moderate voters want jobs, Prosperity of 50 or more and a Treasury above 5 (-2 Public Support if the Full version passes). The upside: any nation that wants the lane must sign a treaty with us.',
    whoPays:
      'The author: 3 Treasury now, its own lane income while the Treaty lasts, new drilling, and 2 Public Support. Every other nation: 1 Treasury now. Nations with navies may no longer send warships through the Arctic, and must sign a treaty with the Arctic nation to earn from the lane.',
    chips:
      'Alliance Builder: treaties and embassies cost us 1 less. Arctic access: under the Full Treaty only our treaty partners may use the lane. Clean Energy Investment: we can pay for a clean switch in another nation. The Grand Coalition Summit. Our same-meeting votes on Feed the World and the Just Transition.',
    naturalAllies: ['meridia', 'vellmark'],
    naturalOpponents: ['kestran', 'aurem'],
    conflicts: ['kestran', 'aurem'],
    complements: ['vellmark', 'ironvale', 'alvera'],
    doctrine: { ironvale: 0, meridia: 2, kestran: -1, vellmark: 1, alvera: 0, karsova: 0, aurem: -1 },
    lobbyOffer: { treasury: 2, energy: 0, food: 0 },
    exposure: ['arctic'],
  },

  aurem: {
    id: 'aurem',
    author: 'aurem',
    title: 'Just Transition Compact',
    amendedTitle: 'Transition Dialogue',
    reservedTurn: 9,
    lead: {
      does: 'A Transition Window helps nations that live on oil, gas or forest land to change fairly. Nations that start to change keep a fair oil price.',
      costs: 'Every nation that does not draw pays 1 Treasury at each Assembly. The author pays 2 from its national fund.',
    },
    fullText:
      'The world helps nations that depend on oil, gas or forest land change fairly. At this and each later Assembly, every nation that does not draw from a Transition Window pays 1 Treasury into it, and Aurem pays 2 from its Sovereign Wealth Fund. Any nation with Carbon 3 or more, or with a rainforest, may draw a share (up to 2 Treasury) to spend within 2 turns on a clean switch, new industries or job training. A nation that draws may never again open new fossil projects or clear forest. Until Turn 16, nations that have started to change still get the old oil price. Forest nations get Forest Payments at Carbon 2 or less.',
    amendedText:
      'Forest Payments start at Carbon 2 or less. Nations with 1 new-industry level keep the old oil price until Turn 14. Paying into the climate effort stays voluntary.',
    speech:
      'The world is right to move beyond oil, but workers and families in oil lands should not be left behind. This Compact helps every nation that changes, and it keeps a fair price for those that start. We pay first, from our own national fund, at every meeting.',
    whyUs:
      'We are the only nation whose income falls when the world succeeds at climate action. From Turn 12, or from the clean-tech tipping point, the World Market pays us only 1 Treasury for 3 Energy unless we have built 2 new-industry levels. Our Royal Grants cost 1 Treasury a turn. Our rainforest can die back from Stage 3, costing 20 Environment, unless our Carbon is 1 or less.',
    dilemma:
      'We must speed up the very change that ends our oil wealth. We pay 2 Treasury from our national fund at every Assembly, and we cannot draw from the Window ourselves. We keep the old oil price after Turn 14 only if we cut our Carbon to 1. Our Traditional Elders want slow change, while our Young Professionals and Merchant Houses want it fast. Low-carbon nations are asked to pay to help high-carbon nations change, and some will call that unfair.',
    whoPays:
      'The author: 2 Treasury from its national fund at passage and at each later Assembly. Every nation that does not draw at a meeting: 1 Treasury at that meeting. Every drawer: a promise never to expand fossil fuels or clear forest.',
    chips:
      'Sovereign Wealth Fund: 12 locked Treasury that grows each turn. Energy exports. The Vision Decree: +10 Prosperity and a free solar switch after 2 turns. The Broker role on other nations’ treaties. The Grand Bargain: our Full vote on a Delta Pact upgrade for a Full vote here.',
    naturalAllies: ['ironvale', 'kestran', 'karsova'],
    naturalOpponents: ['brightwater', 'vellmark'],
    conflicts: ['vellmark', 'meridia', 'brightwater'],
    complements: ['vellmark', 'kestran', 'ironvale'],
    doctrine: { ironvale: 1, brightwater: -1, meridia: 0, kestran: 1, vellmark: -1, alvera: 0, karsova: 1 },
    lobbyOffer: { treasury: 0, energy: 2, food: 0 },
    exposure: ['oil_decline'],
  },

  alvera: {
    id: 'alvera',
    author: 'alvera',
    title: 'Dignity in Displacement Compact',
    amendedTitle: 'Solidarity Platform',
    reservedTurn: 13,
    lead: {
      does: 'When families are forced from home, every nation shares the work. Low islands are promised a new home.',
      costs: 'Each host nation takes a fair share of groups, by welcoming or by paying, or pays a 3 Treasury fee.',
    },
    fullText:
      'When floods, rising seas or drought force families from their homes, every nation shares the work. Each nation helps in the way that fits it: by welcoming families (1 Food per group) or by paying for their resettlement (2 Treasury per group). Each nation that is not a home of displaced families has a share, based on the number of groups. The nations with the most Food take any extra group. A nation that does not meet its share pays a 3 Treasury solidarity fee. Nations that welcome families lose no Security. Home nations pay 1 Treasury for each of their own groups. If every group finds help, home nations lose no Well-being. Low-lying islands are promised a new home for their people if the sea takes an island.',
    amendedText:
      'Each nation may choose whether to welcome families. Nations that welcome families lose no Security. Island nations keep the promise of a new home.',
    speech:
      'Families forced from home by the sea or the drought are not a burden to push away; they are our neighbors. This Compact lets every nation help in its own way, by welcoming or by paying, so no one carries it alone. We pay for our own families first.',
    whyUs:
      'Our islands are always a home of displaced families at Turn 14: cyclones, salt in the wells and rising seas. Every group that finds no host costs home nations up to 4 Well-being. At Stage 4 the sea can take one of our island groups for good. We add almost nothing to warming (Carbon 0). A home nation cannot host, so our humane response must be to pay for resettlement elsewhere (2 Treasury per group).',
    dilemma:
      'We believe choices belong at the most local level that works, yet this Compact asks every nation to take a share of a world problem. Our belief in local choice and our belief in solidarity pull against each other. The Compact also asks home nations, us included, to pay toward their own families. We are a small island economy and cannot buy many votes. At the same meeting another plan helps families stay home and keeps hosting a national choice.',
    whoPays:
      'Host nations (every nation that is not a home of displaced families): a fair share of groups, paid as 1 Food or 2 Treasury per group, or a 3 Treasury solidarity fee. Home nations: 1 Treasury per own group.',
    chips:
      'Solidarity aid: every aid gift we send delivers 1 extra unit. Navy 3, the biggest starting navy. Mediator of Nations: +5 Security to two nations that sign a treaty. Co-sponsor seats, and our earlier votes on the Delta Pact, the Sea Patrol and Feed the World.',
    naturalAllies: ['brightwater', 'vellmark'],
    naturalOpponents: ['karsova', 'aurem'],
    conflicts: ['karsova'],
    complements: ['vellmark', 'kestran', 'ironvale'],
    doctrine: { ironvale: 0, brightwater: 2, meridia: 0, kestran: 0, vellmark: 1, karsova: -1, aurem: -1 },
    lobbyOffer: { treasury: 0, energy: 0, food: 2 },
    exposure: ['displacement'],
  },

  karsova: {
    id: 'karsova',
    author: 'karsova',
    title: 'Right to Stay Compact',
    amendedTitle: 'Drought Early Warning Network',
    reservedTurn: 13,
    lead: {
      does: 'A Dry Lands Water Fund helps families stay home: drought hits get smaller, and homes of displaced families get free flood and storm defenses.',
      costs: 'Every nation pays 1 Treasury now. The author lifts its long-standing tariffs on every nation that pays.',
    },
    fullText:
      'Families should not have to leave home because their land dries out. Every nation pays 1 Treasury now into a Dry Lands Water Fund. The Fund builds wells, canals and farms where drought hits hardest: their drought Food losses are 1 smaller, and from Danger (Stage 3) they grow 1 extra Food per turn. Each nation whose families may be forced from home gets one free level of Resilience now, so fewer families must leave. The Fund also pays to resettle some families who still must move. Heat-Safe Work costs 1 for everyone. Each nation still decides for itself how it helps refugees. In return, Karsova lifts its long-standing tariffs on every nation that paid.',
    amendedText:
      'Every nation’s drought Food losses are 1 smaller, and Heat-Safe Work costs 1. No Fund, no free Resilience, and no change to tariffs or refugee rules.',
    speech:
      'The best help for a family is a home it never has to leave. This Compact builds wells and flood walls where the need is greatest, and it leaves each nation free to decide how it helps refugees. In return we lift our long-standing tariffs on every nation that joins.',
    whyUs:
      'Our land grows little food and dries out first. We take the biggest drought hit in the game at Turn 6, the biggest heat drain on work (-1 Prosperity a turn at Stage 2, -2 from Stage 3), and drought rolls from Stage 3. From Stage 3 our own families are among those forced from home. Our goal of feeding and powering ourselves for 8 turns in a row makes every lost harvest hurt twice.',
    dilemma:
      'We trust our own people more than distant world bodies. Yet our dry land needs a world fund and the votes of other nations. To win them, we must lift our old tariffs on every partner. Our Heritage League dislikes that (-3 Public Support), and we lose the tariff income. We also ask nations with little drought to help pay for wells far away. At the same meeting, another plan asks every nation to share the duty to take in refugees. Its author also wants families safe at home, so we can make a deal or block each other.',
    whoPays:
      'Every nation: 1 Treasury now. The author: its long-standing tariffs, their income and 3 Public Support (or, if the tariffs are already gone, 3 Treasury and a 2-turn tariff ban). Binding refugee shares apply only in a form that paying can always meet.',
    chips:
      'Our tariffs: the Full Compact lifts them all, and we can lift one early for a pledge. Army units cost us 1 less. Adaptation Aid is cheap for us and counts toward our fair share; the Delta is a natural target. A strong Treasury and Energy. Strategic Autonomy. Our earlier votes on the Open Markets Charter, the Sea Patrol and Feed the World.',
    naturalAllies: ['aurem', 'ironvale'],
    naturalOpponents: ['vellmark', 'alvera'],
    conflicts: ['alvera', 'ironvale', 'meridia'],
    complements: ['meridia', 'vellmark', 'ironvale'],
    doctrine: { ironvale: 1, brightwater: 0, meridia: 0, kestran: 0, vellmark: -1, alvera: -1, aurem: 2 },
    lobbyOffer: { treasury: 0, energy: 2, food: 0 },
    exposure: ['drought', 'displacement'],
  },
};

/** Named package deals the UI offers as pledge templates (and bots use between AI authors). */
export interface AgendaPackage {
  id: string;
  name: string;
  a: CountryId;
  b: CountryId;
  /** What `a` pledges on `b`'s agenda and `b` on `a`'s. */
  aPledges: 'full' | 'amended';
  bPledges: 'full' | 'amended';
  text: string;
}

export const AGENDA_PACKAGES: AgendaPackage[] = [
  { id: 'grand_bargain', name: 'Grand Bargain', a: 'aurem', b: 'vellmark', aPledges: 'full', bPledges: 'full', text: 'A Full vote on the Delta Pact (or its upgrade) for a Full vote on the Just Transition Compact.' },
  { id: 'food_for_carbon', name: 'Food for Carbon', a: 'ironvale', b: 'vellmark', aPledges: 'full', bPledges: 'full', text: 'A Full vote on the Delta Pact at Turn 5 for a Full vote on Feed the World at Turn 9.' },
  { id: 'two_harbors', name: 'Two Harbors', a: 'kestran', b: 'meridia', aPledges: 'full', bPledges: 'full', text: 'Full votes on the Sea Patrol Accord and the Open Markets Charter at Turn 5.' },
  { id: 'northern_deal', name: 'Northern Deal', a: 'ironvale', b: 'brightwater', aPledges: 'full', bPledges: 'full', text: 'Full votes on Feed the World and the Arctic Treaty at Turn 9.' },
  { id: 'stay_or_welcome', name: 'Stay or Welcome', a: 'alvera', b: 'karsova', aPledges: 'amended', bPledges: 'amended', text: 'Amended votes on each other’s displacement agendas at Turn 13.' },
];

/** Template chat lines for AI nations (never a language model). */
export const AGENDA_MESSAGES = {
  offer: [
    'We are asking for your Full vote on our World Agenda. This offer is held in escrow until you answer.',
    'Our World Agenda needs your Full vote. We have put this offer in escrow for you.',
  ],
  cosponsorInvite: ['Would your nation co-sponsor our World Agenda? We would welcome you as a partner.'],
  package: ['We propose a package: our vote on your agenda for your vote on ours.'],
  pledgeYes: ['We accept. You have our vote.'],
  pledgeNo: ['We cannot promise our vote on these terms.'],
} as const;

/* ---------------------------------------------------------------------------------------------- */
/* "Our land" notes for the Turn 0 briefing and the Our Nation panel                              */
/* ---------------------------------------------------------------------------------------------- */

/**
 * Plain notes on each slot. Where a land starts richer, poorer or more exposed, the note says it comes from
 * geography, not beliefs: any nation placed there would face the same.
 */
export const SLOT_NOTES: Record<SlotId, { geographyNote: string; advantages: string[] }> = {
  harbor: {
    geographyNote: 'Any nation placed here would bring in Energy and Food by sea, so pirates and storm surges hit its trade hardest.',
    advantages: ['The world’s busiest ports: trade treaties pay extra (Trade Hub).', 'A large Treasury and strong industry.'],
  },
  heartland: {
    geographyNote: 'Any nation placed here would be the world’s breadbasket, and heat attacks exactly that: steady Food losses grow with every stage.',
    advantages: ['Big harvests and a Food surplus at the start.', 'Rivers that flow to other nations (Water Treaties pay you).'],
  },
  sunbelt: {
    geographyNote: 'Any nation placed here would dry out first: the biggest drought hits and the biggest heat drain on work.',
    advantages: ['Strong Treasury and Energy.', 'Cheap solar power: every Carbon cut adds solar export income.'],
  },
  boreal: {
    geographyNote: 'Any nation placed here would have the only Arctic coast and permafrost: early warming brings some gains, then thaw costs and methane risk.',
    advantages: ['A new sea lane and northern farms as the ice melts.', 'Rivers that flow to other nations (Water Treaties pay you).'],
  },
  atoll: {
    geographyNote: 'Any nation placed here would face rising seas, cyclones and salt in the wells, and its families are always among those forced from home.',
    advantages: ['Almost no Carbon to cut.', 'A strong navy, an extra vote on standing climate items, and caps on climate damage.'],
  },
  delta: {
    geographyNote: 'Any nation placed here would sit below three rivers: from Danger, big river floods come often, and Resilience counts at most 1 against water hits.',
    advantages: ['The world’s biggest Food surplus.', 'The largest army, and Army units cost 1 less.'],
  },
  highland: {
    geographyNote: 'Any nation placed here would be the poorest, because its goods must cross other borders to reach the sea. This comes from the land, not from beliefs.',
    advantages: ['Mountains that are hard to attack.', 'Cheap clean power (a Green Transition costs 2) and rivers that flow to other nations.'],
  },
  canopy: {
    geographyNote: 'Any nation placed here would hold a great rainforest: a forest that can die back from Danger, and Forest Payments while its Carbon stays low.',
    advantages: ['Forest Payments from the climate fund while Carbon stays low.', 'Land that can be farmed, and Energy to sell.'],
  },
};
